import { createEmitter } from "../../Utilities/events";
import { CastingConnectionState } from "./CastingEvents";

/**
 * Lab / CI simulated cast target — exercises CastingManager without hardware.
 * Not used in production white-label configs unless features.castingSimulate is on.
 */
export function createSimulatedCastProvider(options) {
  options = options || {};
  var emitter = createEmitter();
  var state = CastingConnectionState.DISCONNECTED;
  var available = options.available !== false;
  var remote = {
    target: "local",
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    playbackRate: 1,
  };
  var syncTimer = null;
  var devices = [
    {
      id: "sim-living-room",
      name: options.deviceName || "Simulated Living Room TV",
      provider: "simulated",
      connected: false,
    },
  ];

  function setState(next) {
    state = next;
    emitter.emit("connectionState", state);
  }

  function tick() {
    if (state === CastingConnectionState.CONNECTED && remote.playing) {
      remote.currentTime += 1;
      if (remote.duration && remote.currentTime >= remote.duration) {
        remote.playing = false;
        remote.currentTime = remote.duration;
      }
    }
    emitter.emit("remoteState", Object.assign({}, remote, { target: "remote" }));
  }

  return {
    name: "simulated",
    getCapabilities: function () {
      return {
        id: "simulated",
        name: "Simulated Cast",
        discover: true,
        play: true,
        pause: true,
        seek: true,
        stop: true,
        volume: true,
        mute: true,
        playbackRate: true,
        textTracks: true,
        audioTracks: true,
        reconnect: true,
        simulated: true,
      };
    },
    initialize: function () {
      emitter.emit("availability", available);
      emitter.emit("devices", devices.slice());
      return Promise.resolve();
    },
    isAvailable: function () {
      return available;
    },
    discoverDevices: function () {
      return Promise.resolve(devices.slice());
    },
    getDevices: function () {
      return devices.slice();
    },
    connect: function (deviceId) {
      setState(CastingConnectionState.CONNECTING);
      emitter.emit("session", { type: "starting", deviceId: deviceId });
      return new Promise(function (resolve) {
        setTimeout(function () {
          setState(CastingConnectionState.CONNECTED);
          devices[0].connected = true;
          remote.target = "remote";
          emitter.emit("devices", devices.slice());
          emitter.emit("session", { type: "started" });
          syncTimer = setInterval(tick, 1000);
          resolve();
        }, 400);
      });
    },
    disconnect: function () {
      if (syncTimer) clearInterval(syncTimer);
      syncTimer = null;
      devices[0].connected = false;
      remote.playing = false;
      remote.target = "local";
      setState(CastingConnectionState.DISCONNECTED);
      emitter.emit("session", { type: "ended", reason: "user" });
      emitter.emit("devices", devices.slice());
      return Promise.resolve();
    },
    loadMedia: function (media) {
      media = media || {};
      remote.duration = media.duration || 600;
      remote.currentTime = media.currentTime || 0;
      remote.playing = media.autoplay !== false;
      remote.volume = media.volume != null ? media.volume : 1;
      remote.muted = !!media.muted;
      remote.playbackRate = media.playbackRate || 1;
      remote.textTrackId = media.textTrack && media.textTrack.id;
      remote.audioTrackId = media.audioTrack && media.audioTrack.id;
      emitter.emit("remoteState", Object.assign({}, remote, { target: "remote" }));
      return Promise.resolve();
    },
    play: function () {
      remote.playing = true;
      return Promise.resolve();
    },
    pause: function () {
      remote.playing = false;
      return Promise.resolve();
    },
    seek: function (t) {
      remote.currentTime = t;
      return Promise.resolve();
    },
    stop: function () {
      remote.playing = false;
      remote.currentTime = 0;
      return this.disconnect();
    },
    setVolume: function (v) {
      remote.volume = v;
      return Promise.resolve();
    },
    setMuted: function (m) {
      remote.muted = !!m;
      return Promise.resolve();
    },
    synchronizeState: function (snap) {
      if (snap) {
        if (snap.currentTime != null) remote.currentTime = snap.currentTime;
        if (snap.volume != null) remote.volume = snap.volume;
        if (snap.muted != null) remote.muted = snap.muted;
        if (snap.playing != null) remote.playing = snap.playing;
        if (snap.playbackRate != null) remote.playbackRate = snap.playbackRate;
        if (snap.duration) remote.duration = snap.duration;
      }
      return Promise.resolve(Object.assign({}, remote, { target: "remote" }));
    },
    getRemoteState: function () {
      return Object.assign({}, remote, {
        target: state === CastingConnectionState.CONNECTED ? "remote" : "local",
      });
    },
    getConnectionState: function () {
      return state;
    },
    getLastError: function () {
      return null;
    },
    /** Lab helper: force unexpected disconnect */
    simulateDrop: function () {
      if (syncTimer) clearInterval(syncTimer);
      syncTimer = null;
      devices[0].connected = false;
      setState(CastingConnectionState.DISCONNECTED);
      emitter.emit("session", { type: "ended", reason: "unexpected" });
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      if (syncTimer) clearInterval(syncTimer);
      emitter.removeAllListeners();
    },
  };
}

export default createSimulatedCastProvider;
